/**
 * Tela dedicada ao preview.
 *
 * Por que uma tela e não mais um painel: acompanhar uma conversa exige espaço, e no editor ela
 * dividia a largura com paleta, canvas e inspector.
 *
 * Ela carrega o rascunho **da fonte de dados**, não do store em memória do editor. Isso é de
 * propósito: assim a tela funciona aberta numa segunda aba (ou num segundo monitor) enquanto o
 * editor segue em uso na primeira. O preço é que edições recém-feitas só aparecem depois do
 * autosave — daí o botão "Recarregar rascunho".
 *
 * Como o widget é um botão flutuante sobre a página de terceiros, o palco tem um site fictício por
 * baixo e um seletor de tamanho: posição e deslocamento só podem ser julgados em contexto.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collectPublishBlockers, type TranscriptEntry, type WebchatConfig } from '@merlin/flow-engine';
import type { WidgetEvent, WidgetInstance } from '@merlin/sdk';
import { getDataSource, type WebchatDoc } from '../data/index.js';
import { SimulatedSite } from '../components/SimulatedSite.js';
import { WidgetStage } from '../components/WidgetStage.js';
import { Badge, Button, GroupTitle, Notice, SegmentedControl } from '../components/ui.js';
import { IconeAtualizar, IconeFluxo } from '../components/icons.js';

type Device = 'desktop' | 'mobile';

const DEVICES: { id: Device; label: string; width: number | null; height: number | null }[] = [
  { id: 'desktop', label: 'Computador', width: null, height: null },
  { id: 'mobile', label: 'Celular', width: 390, height: 844 },
];

const ROLE_LABEL: Record<TranscriptEntry['role'], string> = {
  bot: 'bot',
  visitor: 'visitante',
  system: 'sistema',
};

const ROLE_STYLE: Record<TranscriptEntry['role'], string> = {
  bot: 'bg-slate-100 text-slate-700',
  visitor: 'bg-sky-100 text-sky-800',
  system: 'bg-amber-100 text-amber-900',
};

export function PreviewPage() {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<WebchatDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [generation, setGeneration] = useState(0);
  const [variables, setVariables] = useState<Record<string, unknown>>({});
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [status, setStatus] = useState<'parado' | 'rodando' | 'fim'>('parado');
  // `null` enquanto o palco ainda nao foi medido; < 1 quando a moldura precisou ser reduzida.
  const [stageScale, setStageScale] = useState<number | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const loaded = await getDataSource().get(id);
      if (!loaded) {
        setError('Webchat não encontrado.');
        return;
      }
      setDoc(loaded);
      setVariables({});
      setTranscript([]);
      setStatus('parado');
      setGeneration((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const blockers = useMemo(
    () => (doc ? collectPublishBlockers(doc.draft, doc.authorizedDomains) : []),
    [doc],
  );

  const config = useMemo<WebchatConfig | null>(
    () =>
      doc
        ? {
            publicKey: doc.publicKey,
            name: doc.name,
            authorizedDomains: doc.authorizedDomains.length ? doc.authorizedDomains : ['localhost'],
            appearance: doc.draft.appearance,
            flow: doc.draft.flow,
            version: doc.published?.version ?? 0,
          }
        : null,
    [doc],
  );

  const handleEvent = useCallback((event: WidgetEvent, instance: WidgetInstance) => {
    if (event.type === 'start') setStatus('rodando');
    if (event.type === 'end') setStatus('fim');
    setVariables(instance.getVariables());
    setTranscript(instance.getTranscript());
  }, []);

  const selected = DEVICES.find((item) => item.id === device) ?? DEVICES[0]!;

  if (loading && !doc) return <div className="p-8 text-sm text-slate-500">Carregando…</div>;

  if (error) {
    return (
      <div className="space-y-3 p-8">
        <Notice tone="danger" title="Não foi possível abrir o preview">
          {error}
        </Notice>
        <Link to="/" className="text-sm text-slate-700 underline">
          voltar para a lista
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-100">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <Link
          to={`/w/${id}`}
          className="rounded text-sm text-slate-500 transition hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          ← Editor
        </Link>
        <span className="text-sm font-semibold text-slate-900">{doc?.name}</span>
        <Badge tone="info">rascunho</Badge>
        <Badge tone={status === 'fim' ? 'ok' : status === 'rodando' ? 'info' : 'neutral'} dot>
          {status}
        </Badge>
        {/*
          Selo de escala: sem ele, moldura reduzida da' a impressao de que o widget encolheu, quando
          na verdade a proporcao esta' fiel e so' o tamanho na tela mudou.
        */}
        {selected.width && stageScale !== null && stageScale < 0.995 ? (
          <span
            className="text-xs text-slate-500"
            title="A moldura foi reduzida para caber na janela. As proporções são as de um celular de verdade."
          >
            {selected.width}×{selected.height} · {Math.round(stageScale * 100)}%
          </span>
        ) : null}

        {/*
          Segmentado, e não dois botões emendados: as opções são exclusivas e trocam a lente sobre o
          mesmo palco. `radiogroup` é o papel que diz isso — e é o que faz as setas do teclado
          funcionarem como a pessoa espera de um seletor.
        */}
        <SegmentedControl<Device>
          label="Tamanho de tela"
          options={DEVICES.map((item) => ({ id: item.id, label: item.label }))}
          value={device}
          onChange={(proximo) => {
            setDevice(proximo);
            setGeneration((value) => value + 1);
          }}
        />

        <div className="ml-auto flex items-center gap-2">
          <Button
            onClick={() => {
              setVariables({});
              setTranscript([]);
              setStatus('parado');
              setGeneration((value) => value + 1);
            }}
          >
            <IconeFluxo />
            Reiniciar conversa
          </Button>
          <Button onClick={() => void load()}>
            <IconeAtualizar />
            Recarregar rascunho
          </Button>
        </div>
      </header>

      {blockers.length > 0 ? (
        <div className="px-4 pt-3">
          <Notice tone="warn" title="O fluxo está incompleto, então a conversa não roda">
            <ul className="list-disc space-y-1 pl-4">
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </Notice>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <main className="flex min-w-0 flex-1 items-center justify-center overflow-hidden p-6">
          {config && blockers.length === 0 ? (
            /*
             * No modo celular o palco vira uma viewport de verdade: `fit` informa ao widget que a
             * tela tem 390px (via `--merlin-viewport-*`), então a janela de chat se estreita como
             * num telefone em vez de estourar a moldura. No modo computador não há `fit` — o palco
             * é o próprio espaço disponível, em escala 1:1.
             */
            <WidgetStage
              config={config}
              generation={generation}
              onEvent={handleEvent}
              onScaleChange={setStageScale}
              className={
                selected.width
                  ? 'h-full w-full'
                  : 'relative h-full w-full overflow-hidden rounded-xl border border-slate-300 bg-white shadow-lg'
              }
              {...(selected.width && selected.height
                ? {
                    fit: { width: selected.width, height: selected.height },
                    /*
                     * Moldura por `box-shadow`, nao por `border`: com `box-sizing: border-box` uma
                     * borda de 6px comeria 12px da largura, e a viewport declarada ao widget
                     * (390px) passaria a nao corresponder ao espaco real (378px) — era o que fazia
                     * a janela de chat ultrapassar a parede esquerda. `box-shadow` nao ocupa
                     * espaco de layout.
                     */
                    stageClassName:
                      'rounded-[26px] shadow-[0_0_0_6px_#1e293b,0_25px_50px_-12px_rgba(15,23,42,0.45)]',
                  }
                : {})}
            >
              <SimulatedSite title={doc?.name ?? ''} />
            </WidgetStage>
          ) : (
            <div className="relative h-full w-full overflow-hidden rounded-xl border border-slate-300 bg-white shadow-lg">
              <SimulatedSite title={doc?.name ?? ''} />
            </div>
          )}
        </main>

        {/*
          `slate-500` no lugar de `slate-400` nos textos de estado vazio: sobre branco o 400 dá 2,9:1,
          abaixo dos 4,5:1 da WCAG AA. E 14px no lugar de 12px, porque são frases, não rótulos.
        */}
        <aside aria-label="Estado da conversa" className="flex w-80 shrink-0 flex-col border-l border-slate-200 bg-white">
          <div className="px-4 pt-4">
            <GroupTitle>Variáveis coletadas</GroupTitle>
          </div>
          <div className="max-h-56 overflow-auto px-4 py-3">
            {Object.keys(variables).length === 0 ? (
              <p className="text-sm text-slate-500">nenhuma ainda</p>
            ) : (
              <dl className="space-y-1 text-xs">
                {Object.entries(variables).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-2">
                    <dt className="truncate font-mono text-slate-500">{key}</dt>
                    <dd className="truncate font-mono text-slate-800" title={JSON.stringify(value)}>
                      {JSON.stringify(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          <div className="border-t border-slate-200 px-4 pt-4">
            <GroupTitle>Transcrição</GroupTitle>
          </div>
          <div className="flex-1 space-y-2 overflow-auto px-4 py-3">
            {transcript.length === 0 ? (
              <p className="text-sm text-slate-500">a conversa aparece aqui conforme avança</p>
            ) : (
              transcript.map((entry, index) => (
                <div key={index} className="text-sm">
                  <span
                    className={`mr-2 inline-block rounded px-2 py-1 text-xs leading-none font-bold uppercase ${ROLE_STYLE[entry.role]}`}
                  >
                    {ROLE_LABEL[entry.role]}
                  </span>
                  <span className="text-slate-700">{entry.text}</span>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-slate-200 p-4">
            <Notice tone="info">
              Dois blocos se comportam diferente aqui: código em modo <code>page</code> roda no
              sandbox (executar código de terceiros no painel não se justifica) e o webhook de backend
              não executa, porque o servidor só conhece a versão publicada.
            </Notice>
          </div>
        </aside>
      </div>
    </div>
  );
}
